// src/App.jsx
import React, { useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { ThemeProvider } from "next-themes";
import { Toaster } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import RouteErrorBoundary from "./components/common/RouteErrorBoundary";
import store from "./store/store";

// Layouts & Pages
import MainLayout from "./components/layout/MainLayout";
import LoginPage from "./pages/auth/LoginPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import SecurityQuestionLoginPage from "./pages/auth/SecurityQuestionLoginPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import SecuritySetupPage from "./pages/auth/SecuritySetupPage";
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const EmployeePage = React.lazy(() => import("./pages/modules/EmployeePage"));
const RolePage = React.lazy(() => import("./pages/modules/RolePage"));
const DepartmentPage = React.lazy(() => import("./pages/modules/DepartmentPage"));
const SecurityEmployeeQuestionPage = React.lazy(() => import("./pages/modules/SecurityEmployeeQuestionPage"));
const CategoryPage = React.lazy(() => import("./pages/modules/CategoryPage"));
const SubcategoryPage = React.lazy(() => import("./pages/modules/SubcategoryPage"));
const InventoryPage = React.lazy(() => import("./pages/modules/InventoryPage"));
const StockPage = React.lazy(() => import("./pages/modules/StockPage"));
const CanteenPage = React.lazy(() => import("./pages/modules/CanteenPage"));
const GeneralLogPage = React.lazy(() => import("./pages/modules/GeneralLogPage"));
const InventoryLogPage = React.lazy(() => import("./pages/modules/InventoryLogPage"));
const ProfilePage = React.lazy(() => import("./pages/modules/ProfilePage"));
const RaiseTicketPage = React.lazy(() => import("./pages/modules/RaiseTicketPage"));
const RequestIssuancePage = React.lazy(() => import("./pages/modules/RequestIssuancePage"));
const NotificationsPage = React.lazy(() => import("./pages/modules/NotificationsPage"));
const AssetAssignPage = React.lazy(() => import("./pages/modules/AssetAssignPage"));

// HOOKS
import usePermissionSocket from "./hooks/usePermissionSocket";
import { fetchMyPermissions } from "./store/slices/permissionSlice";
import { usePermissions } from "./hooks/usePermissions";
const SuggestionPage = React.lazy(() => import("./pages/modules/SuggestionPage"));
const BreakTimeLogPage = React.lazy(() => import("./pages/modules/BreakTimeLogPage"));
const RequestLogsPage = React.lazy(() => import("./pages/modules/RequestLogsPage"));
const AssetAssignLogsPage = React.lazy(() => import("./pages/modules/AssetAssignLogsPage"));
const TicketLogsPage = React.lazy(() => import("./pages/modules/TicketLogsPage"));
const UpdateWalletPage = React.lazy(() => import("./pages/modules/UpdateWalletPage"));
const CanteenLogsPage = React.lazy(() => import("./pages/modules/CanteenLogsPage"));
const OrderListPage = React.lazy(() => import("./pages/modules/OrderListPage"));
const CustomerProfilePage = React.lazy(() => import("./pages/CustomerProfilePage"));
const SettingsPage = React.lazy(() => import("./pages/modules/SettingsPage"));
const FeedbackPage = React.lazy(() => import("./pages/modules/FeedbackPage"));
const ReportPage = React.lazy(() => import("./pages/modules/ReportPage"));
import { Loader2 } from "lucide-react";
const EscalationHistoryLogPage = React.lazy(() => import("./pages/modules/EscalationHistoryLogPage"));
const EmployeeLogPage = React.lazy(() => import("./pages/modules/EmployeeLogPage"));
const PermissionsLogPage = React.lazy(() => import("./pages/modules/PermissionsLogPage"));
const BreakLogPage = React.lazy(() => import("./pages/modules/BreakLogPage"));
const MyTeamPage = React.lazy(() => import("./pages/modules/MyTeamPage"));
const CompanyPage = React.lazy(() => import("./pages/modules/CompanyPage"));
const VendorPage = React.lazy(() => import("./pages/modules/VendorPage"));
const InventorySummaryPage = React.lazy(() => import("./pages/reports/InventorySummaryPage"));
const StockMovementPage = React.lazy(() => import("./pages/reports/StockMovementPage"));
const LowStockAlertPage = React.lazy(() => import("./pages/reports/LowStockAlertPage"));
const ExpiredItemsPage = React.lazy(() => import("./pages/reports/ExpiredItemsPage"));
const ConsumptionTrendPage = React.lazy(() => import("./pages/reports/ConsumptionTrendPage"));
const DepartmentConsumptionPage = React.lazy(() => import("./pages/reports/DepartmentConsumptionPage"));
const PurchaseHistoryPage = React.lazy(() => import("./pages/reports/PurchaseHistoryPage"));
const PriceVariationPage = React.lazy(() => import("./pages/reports/PriceVariationPage"));
const AssetMasterPage = React.lazy(() => import("./pages/reports/AssetMasterPage"));
const BreakSummaryPage = React.lazy(() => import("./pages/reports/BreakSummaryPage"));
const BreakDurationPage = React.lazy(() => import("./pages/reports/BreakDurationPage"));
const IrregularBreaksPage = React.lazy(() => import("./pages/reports/IrregularBreaksPage"));
const TimeDeviationPage = React.lazy(() => import("./pages/reports/TimeDeviationPage"));
const TicketsOverviewPage = React.lazy(() => import("./pages/reports/TicketsOverviewPage"));
const TatCompliancePage = React.lazy(() => import("./pages/reports/TatCompliancePage"));
const EscalationsPage = React.lazy(() => import("./pages/reports/EscalationsPage"));
const DepartmentTicketsPage = React.lazy(() => import("./pages/reports/DepartmentTicketsPage"));
const RecurringIssuesPage = React.lazy(() => import("./pages/reports/RecurringIssuesPage"));
const HandlerProductivityPage = React.lazy(() => import("./pages/reports/HandlerProductivityPage"));
const CanteenOrdersPage = React.lazy(() => import("./pages/reports/CanteenOrdersPage"));
const PopularItemsPage = React.lazy(() => import("./pages/reports/PopularItemsPage"));
const CanteenFinancePage = React.lazy(() => import("./pages/reports/CanteenFinancePage"));
const IngredientStockPage = React.lazy(() => import("./pages/reports/IngredientStockPage"));
import ReportLayout from "./components/layout/ReportLayout";
import { NotFoundPage, AccessDeniedPage } from "./pages/StatusPage";
const AttendancePage = React.lazy(() => import("./pages/modules/AttendancePage"));
const LeaveManagementPage = React.lazy(() => import("./pages/modules/LeaveManagementPage"));
const ExhibitionPage = React.lazy(() => import("./pages/modules/ExhibitionPage"));
const ContactPage = React.lazy(() => import("./pages/modules/ContactPage"));
const HourlyWorkLogPage = React.lazy(() => import("./pages/modules/HourlyWorkLogPage"));
const LeavePolicyPage = React.lazy(() => import("./pages/modules/LeavePolicyPage"));
const ToDoListPage = React.lazy(() => import("./pages/modules/ToDoListPage"));
const MarketingLeadManagementPage = React.lazy(() => import("./pages/modules/MarketingLeadManagementPage"));
const LeadsPage = React.lazy(() => import("./pages/modules/LeadsPage"));
const LeadStatusesPage = React.lazy(() => import("./pages/modules/LeadStatusesPage"));
const ProductsPage = React.lazy(() => import("./pages/modules/ProductsPage"));
const MessagesPage = React.lazy(() => import("./pages/modules/MessagesPage"));
const OtherContactsPage = React.lazy(() => import("./pages/modules/OtherContactsPage"));
const CampaignsPage = React.lazy(() => import("./pages/modules/CampaignsPage"));
const ContactGroupsPage = React.lazy(() => import("./pages/modules/ContactGroupsPage"));
const DemoSchedulesPage = React.lazy(() => import("./pages/modules/DemoSchedulesPage"));
const CategoriesPage = React.lazy(() => import("./pages/modules/CategoriesPage"));
const WarrantyPage = React.lazy(() => import("./pages/modules/WarrantyPage"));
const ToDoCategoryPage = React.lazy(() => import("./pages/modules/ToDoCategoryPage"));
import useFirebaseNotifications from "./hooks/useFirebaseNotifications";
const MyAttachmentsPage = React.lazy(() => import("./pages/modules/MyAttachmentsPage"));
// import InterviewFormPage from "./pages/auth/InterviewFormPage";
const InterviewApplicationsPage = React.lazy(() => import("./pages/modules/InterviewApplicationsPage"));
const VisitorPublicForm = React.lazy(() => import("./pages/public/VisitorPublicForm"));
const VisitorPublicCheckout = React.lazy(() => import("./pages/public/VisitorPublicCheckout"));
const VisitorPage = React.lazy(() => import("./pages/modules/VisitorPage"));
const AgencyPublicForm = React.lazy(() => import("./pages/public/AgencyPublicForm"));
const AgencyPage = React.lazy(() => import("./pages/modules/AgencyPage"));
const DesignMastersPage = React.lazy(() => import("./pages/modules/DesignMastersPage"));
const CatalogDesignPage = React.lazy(() => import("./pages/modules/CatalogDesignPage"));
const EmployeeLocationPage = React.lazy(() => import("./pages/modules/EmployeeLocationPage"));
const ActivityLogsPage = React.lazy(() => import("./pages/modules/ActivityLogsPage"));
const WorkTaskReportPage = React.lazy(() => import("./pages/modules/WorkTaskReportPage"));
const TargetAchievementReportPage = React.lazy(() => import("./pages/modules/TargetAchievementReportPage"));
const DesignTimeSheetReportPage = React.lazy(() => import("./pages/modules/DesignTimeSheetReportPage"));
const DesignDetailsReportPage = React.lazy(() => import("./pages/modules/DesignDetailsReportPage"));
const EmployeeOccasionTimeReportPage = React.lazy(() => import("./pages/modules/EmployeeOccasionTimeReportPage"));
const WorkDashboardAssignmentPage = React.lazy(() => import("./pages/modules/WorkDashboardAssignmentPage"));
const WorkTrackerDashboard = React.lazy(() => import("./pages/modules/WorkTrackerDashboard"));
const WorkTaskBoard = React.lazy(() => import("./pages/modules/WorkTaskBoard"));
// import { fetchNotificationChoices } from './store/slices/notificationSlice';
// import { initNotificationRoutes } from "./utlis/notificationRoutes";

// --- SECURITY LOCK: BLOCK BACK + URL TAMPERING ---
const useSecurityLock = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isFirstLogin = localStorage.getItem("isFirstLogin") === "true";
  const prevPathnameRef = React.useRef("");

  useEffect(() => {
    if (isFirstLogin && location.pathname !== "/security-setup") {
      navigate("/security-setup", { replace: true });
    }
    prevPathnameRef.current = location.pathname;
  }, [isFirstLogin, location.pathname, navigate]);

  useEffect(() => {
    if (!isFirstLogin) return;

    const push = () => window.history.pushState(null, "", window.location.href);
    push();

    const onPop = () => {
      if (prevPathnameRef.current === "/security-setup") {
        push();
      } else {
        prevPathnameRef.current = location.pathname;
      }
    };

    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [isFirstLogin, location.pathname]);
};

// ──────────────────────────────────────────────────────────────────────
// ROUTE GUARDS
// ──────────────────────────────────────────────────────────────────────
const getIsFirstLogin = () => localStorage.getItem("isFirstLogin") === "true";

const PublicRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const first = getIsFirstLogin();

  if (token && !first) return <Navigate to="/dashboard" replace />;
  // if (token && first) return <Navigate to="/security-setup" replace />;
  return <>{children}</>;
};

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const first = getIsFirstLogin();

  if (!token) return <Navigate to="/login" replace />;
  // Security-question setup is mandatory. The server enforces this too
  // (backend/first_login.py) — this guard is only so the user lands in the right place.
  if (first) return <Navigate to="/security-setup" replace />;
  return <>{children}</>;
};

// Signing out in one tab signs out every other tab: without this, a second tab kept
// rendering the previous user's screens and sent the new user's token with them.
const SessionSync = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "logout-at" && e.key !== "token") return;
      if (localStorage.getItem("token")) return; // still signed in
      if (window.location.pathname.startsWith("/login")) return;
      navigate("/login", { replace: true });
      window.location.reload();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [navigate]);
  return null;
};

// Loads the caller's own permissions over HTTP once, so gated routes can decide even if
// the permission websocket never connects. The socket then keeps them live.
const PermissionBootstrap = () => {
  const dispatch = useDispatch();
  const token = useSelector((s) => s.auth?.token);
  useEffect(() => {
    if (token) dispatch(fetchMyPermissions());
  }, [token, dispatch]);
  return null;
};

const SecuritySetupRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const first = getIsFirstLogin();

  if (!token || !first) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

// --- SECURITY LOCK COMPONENT (inside Provider) ---
const SecurityLock = () => {
  useSecurityLock();
  return null;
};

// --- PERMISSION SOCKET COMPONENT (inside Provider) ---
const PermissionSocket = () => {
  usePermissionSocket();
  return null;
};

const PermissionRoute = ({ children, resource }) => {
  const { canRead, isLoading } = usePermissions(resource);

  // Do not redirect until permissions are 100% loaded
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Now safe to check dashboard permission
  if (resource === "dashboard" && !canRead) {
    return <Navigate to="/my-teams" replace />;
  }

  // Say so, rather than bouncing to the dashboard as if the page didn't exist.
  return canRead ? children : <AccessDeniedPage />;
};


// const NotificationRouteInitializer = () => {
//   const dispatch = useDispatch();
//   const { notificationChoices } = useSelector(s => s.notification);

//   useEffect(() => {
//     dispatch(fetchNotificationChoices());
//   }, [dispatch]);

//   useEffect(() => {
//     if (notificationChoices && notificationChoices.length > 0) {
//       initNotificationRoutes(notificationChoices);
//     }
//   }, [notificationChoices]);

//   return null;
// };

const InitHooks = () => {
  useFirebaseNotifications();
  const token = useSelector((s) => s.auth?.token);
  useEffect(() => {
    if (!token) return;
    // Registers this browser for push. Loads the Firebase SDK on demand, so it is
    // never downloaded for someone who only visits the login page.
    import("./utlis/fcmToken").then(({ getFcmToken }) => getFcmToken());
  }, [token]);
  return null;
};

// --- MAIN APP WITH PROVIDERS ---
function App() {
  // The FCM token is fetched from inside the app (after login) rather than here, so the
  // browser isn't asked for notification permission on the login screen and the Firebase
  // SDK stays out of the first load.
  return (
    <Provider store={store}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <TooltipProvider>
          <Router>
                {/* {(() => {
              useFirebaseNotifications();
              return null;
            })()} */}
            <InitHooks />
               {/* <NotificationRouteInitializer /> */}
            <SecurityLock />
            <SessionSync />
            <PermissionBootstrap />
            <PermissionSocket />
            <div className="App">
              <RouteErrorBoundary>
              <Suspense
                fallback={
                  <div className="flex items-center justify-center min-h-screen">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                }
              >
              <Routes>
                {/* Public */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <PublicRoute>
                      <ForgotPasswordPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/security-question-login"
                  element={
                    <PublicRoute>
                      <SecurityQuestionLoginPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/reset-password/:token"
                  element={
                    <PublicRoute>
                      <ResetPasswordPage />
                    </PublicRoute>
                  }
                />

                <Route
                  path="/interview-form"
                  element={<InterviewApplicationsPage />}
                />
                {/* Public visitor registration (reached from the QR — no login) */}
                <Route
                  path="/visitor-register"
                  element={<VisitorPublicForm />}
                />
                {/* Public visitor self-checkout (a separate gate QR — no login) */}
                <Route
                  path="/visitor-checkout"
                  element={<VisitorPublicCheckout />}
                />
                {/* Public agency form (secure link — no login) */}
                <Route
                  path="/agency-form/:token"
                  element={<AgencyPublicForm />}
                />
                {/* Security Setup */}
                <Route
                  path="/security-setup"
                  element={
                    <SecuritySetupRoute>
                      <SecuritySetupPage />
                    </SecuritySetupRoute>
                  }
                />

                {/* Protected */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route
                    path="dashboard"
                    element={
                      <PermissionRoute resource="dashboard">
                        <Dashboard />
                      </PermissionRoute>
                    }
                  />
                  <Route path="profile" element={<ProfilePage />} />
                  {/* Visitor Management — permission-gated ('visitor'); the list is still scoped
                      to the user server-side (superadmin sees all) */}
                  <Route
                    path="visitors"
                    element={
                      <PermissionRoute resource="visitor">
                        <VisitorPage />
                      </PermissionRoute>
                    }
                  />
                  {/* Agency Agreement Management (permission-gated) */}
                  <Route
                    path="agencies"
                    element={
                      <PermissionRoute resource="agency">
                        <AgencyPage />
                      </PermissionRoute>
                    }
                  />
                  {/* Sampling Design masters (permission-gated) */}
                  <Route
                    path="design-masters"
                    element={
                      <PermissionRoute resource="design_master">
                        <DesignMastersPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="catalog-design"
                    element={
                      <PermissionRoute resource="designed_catalog">
                        <CatalogDesignPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="employee"
                    element={
                      <PermissionRoute resource="employee">
                        <EmployeePage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="my-teams"
                    element={
                      // <PermissionRoute resource="employee">
                      <MyTeamPage />
                      // </PermissionRoute>
                    }
                  />
                  <Route
                    path="/my-attendance"
                    element={
                      <PermissionRoute resource="break_time_log">
                        <AttendancePage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="general-log"
                    element={
                      <PermissionRoute resource="activity_logs">
                        <GeneralLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="employee-log"
                    element={
                      <PermissionRoute resource="employee_log">
                        <EmployeeLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="permissions-log"
                    element={
                      <PermissionRoute resource="permission_log">
                        <PermissionsLogPage />
                      </PermissionRoute>
                    }
                  />

                  <Route
                    path="break-log"
                    element={
                      <PermissionRoute resource="break_log">
                        <BreakLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/new-development-log"
                    element={
                      <PermissionRoute resource="activity_logs">
                        <ActivityLogsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="role"
                    element={
                      <PermissionRoute resource="role">
                        <RolePage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="department"
                    element={
                      <PermissionRoute resource="department">
                        <DepartmentPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="company"
                    element={
                      <PermissionRoute resource="company">
                        <CompanyPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="security-question"
                    element={
                      <PermissionRoute resource="password_reset_questions">
                        <SecurityEmployeeQuestionPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="category"
                    element={
                      <PermissionRoute resource="category">
                        <CategoryPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="subcategory"
                    element={
                      <PermissionRoute resource="sub_category">
                        <SubcategoryPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="vendor"
                    element={
                      <PermissionRoute resource="vendor">
                        <VendorPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="inventory"
                    element={
                      <PermissionRoute resource="inventory">
                        <InventoryPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="inventory-log"
                    element={
                      <PermissionRoute resource="inventory_log">
                        <InventoryLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="stock"
                    element={
                      <PermissionRoute resource="stock">
                        <StockPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="canteen/menu"
                    element={
                      <PermissionRoute resource="menu">
                        <CanteenPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="raise-ticket"
                    element={
                      <PermissionRoute resource="ticket">
                        <RaiseTicketPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="request-issuance"
                    element={
                      <PermissionRoute resource="request">
                        <RequestIssuancePage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={<NotificationsPage />}
                  />
                  <Route
                    path="/asset-assign"
                    element={
                      <PermissionRoute resource="asset_assign">
                        <AssetAssignPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/suggestion"
                    element={
                      <PermissionRoute resource="suggestions">
                        <SuggestionPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/break-time-log"
                    element={
                      <PermissionRoute resource="break_time_log">
                        <BreakTimeLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/request-logs"
                    element={
                      <PermissionRoute resource="request_log">
                        <RequestLogsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/escalation-history-log"
                    element={
                      <PermissionRoute resource="escalation_history">
                        <EscalationHistoryLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/asset-assign-logs"
                    element={
                      <PermissionRoute resource="asset_assign_log">
                        <AssetAssignLogsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/ticket-logs"
                    element={
                      <PermissionRoute resource="ticket_log">
                        <TicketLogsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/canteen-logs"
                    element={
                      <PermissionRoute resource="canteen_log">
                        <CanteenLogsPage />
                      </PermissionRoute>
                    }
                  />

                  <Route
                    path="/update-wallet"
                    element={
                      <PermissionRoute resource="employee_wallet_history">
                        <UpdateWalletPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/order-list"
                    element={
                      <PermissionRoute resource="canteen_order_log">
                        <OrderListPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/customer-profile"
                    element={
                      <PermissionRoute resource="customer_profile">
                        <CustomerProfilePage />
                      </PermissionRoute>
                    }
                  />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route
                    path="work-task-assignment"
                    element={
                      <PermissionRoute resource="work_assign_task">
                        <WorkTaskBoard />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/designing-dashboard"
                    element={
                      <PermissionRoute resource="work_assign_dashboard">
                        <WorkTrackerDashboard />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="leave-management"
                    element={
                      <PermissionRoute resource="leave_management">
                        <LeaveManagementPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="exhibition-management"
                    element={
                      <PermissionRoute resource="exhibition">
                        <ExhibitionPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="contact-details"
                    element={
                      <PermissionRoute resource="new_contact">
                        <ContactPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="hourly-work-log"
                    element={
                      <PermissionRoute resource="hourly_work_management">
                        <HourlyWorkLogPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="leave-policy"
                    element={
                      <PermissionRoute resource="leave_policy">
                        <LeavePolicyPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="to-do-list"
                    element={
                      <PermissionRoute resource="to_do_list">
                        <ToDoListPage />
                      </PermissionRoute>
                    }
                  />

                  {/* ✅ Lead Management - Individual Routes */}
                  {/* ───────────────────────────────────────────────────── */}

                  {/* Leads */}
                  <Route
                    path="/lead-management/leads"
                    element={
                      <PermissionRoute resource="lead">
                        <LeadsPage />
                      </PermissionRoute>
                    }
                  />

                  {/* Lead Status */}
                  <Route
                    path="/lead-management/lead-status"
                    element={
                      <PermissionRoute resource="lead">
                        <LeadStatusesPage />
                      </PermissionRoute>
                    }
                  />

                  {/* Product Details */}
                  <Route
                    path="/lead-management/products"
                    element={
                      <PermissionRoute resource="sales_product">
                        <ProductsPage />
                      </PermissionRoute>
                    }
                  />

                  {/* Messages */}
                  <Route
                    path="/lead-management/messages"
                    element={
                      <PermissionRoute resource="sales_message_log">
                        <MessagesPage />
                      </PermissionRoute>
                    }
                  />

                  {/* Other Contacts */}
                  <Route
                    path="/lead-management/contacts"
                    element={
                      <PermissionRoute resource="sales_contact_list">
                        <OtherContactsPage />
                      </PermissionRoute>
                    }
                  />

                  {/* Campaigns */}
                  <Route
                    path="/lead-management/campaigns"
                    element={
                      <PermissionRoute resource="sales_campaign">
                        <CampaignsPage />
                      </PermissionRoute>
                    }
                  />

                  {/* Contact Groups */}
                  <Route
                    path="/lead-management/contact-groups"
                    element={
                      <PermissionRoute resource="sales_contact_group">
                        <ContactGroupsPage />
                      </PermissionRoute>
                    }
                  />

                  {/* Appointments / Demo Schedules */}
                  <Route
                    path="/lead-management/appointments"
                    element={
                      <PermissionRoute resource="sales_demo_schedule">
                        <DemoSchedulesPage />
                      </PermissionRoute>
                    }
                  />

                  {/* Categories */}
                  <Route
                    path="/lead-management/categories"
                    element={
                      <PermissionRoute resource="sales_category">
                        <CategoriesPage />
                      </PermissionRoute>
                    }
                  />

                  {/* ───────────────────────────────────────────────────── */}
                  {/* End Lead Management Routes */}


                  <Route
                    path="/todo-categories"
                    element={
                      <PermissionRoute resource="tealtik_category">
                        <ToDoCategoryPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="warranty-guarantee-management"
                    element={
                      <PermissionRoute resource="warranty_guarantee">
                        <WarrantyPage />
                      </PermissionRoute>
                    }
                  />


                  <Route
                    path="interview-applications"
                    element={
                      <PermissionRoute resource="interview_form">
                        <InterviewApplicationsPage />
                      </PermissionRoute>
                    }
                  />

                  <Route
                    path="/feedback"
                    element={
                      <PermissionRoute resource="feedback">
                        <FeedbackPage />
                      </PermissionRoute>
                    }
                  />

                  <Route
                    path="/my-attachments"
                    element={
                      <PermissionRoute resource="my_attachment">
                        <MyAttachmentsPage />
                      </PermissionRoute>
                    }
                  />

                  <Route
                    path="/locations"
                    element={
                      <PermissionRoute resource="employee_location">
                        <EmployeeLocationPage />
                      </PermissionRoute>
                    }
                  />
                  {/* <Route
                    path="/reports"
                    element={
                      <PermissionRoute resource="reports">
                        <ReportPage />
                      </PermissionRoute>
                    }
                  /> */}

                  <Route
                    path="/reports"
                    element={
                      <PermissionRoute resource="reports">
                        <ReportLayout />
                      </PermissionRoute>
                    }
                  >
                    <Route
                      index
                      element={
                        <Navigate to="/reports/inventory-summary" replace />
                      }
                    />

                    <Route
                      path="inventory-summary"
                      element={<InventorySummaryPage />}
                    />

                    <Route
                      path="work-task-report"
                      element={<WorkTaskReportPage />}
                    />
                    <Route
                      path="target-achievement"
                      element={<TargetAchievementReportPage />}
                    />
                    <Route
                      path="design-timesheet"
                      element={<DesignTimeSheetReportPage />}
                    />
                    <Route
                      path="design-details"
                      element={<DesignDetailsReportPage />}
                    />
                    <Route
                      path="employee-occasion-time"
                      element={<EmployeeOccasionTimeReportPage />}
                    />
                    <Route
                      path="stock-movement"
                      element={<StockMovementPage />}
                    />
                    <Route path="low-stock" element={<LowStockAlertPage />} />
                    <Route
                      path="expired-items"
                      element={<ExpiredItemsPage />}
                    />
                    <Route
                      path="consumption-trend"
                      element={<ConsumptionTrendPage />}
                    />
                    <Route
                      path="department-consumption"
                      element={<DepartmentConsumptionPage />}
                    />
                    <Route
                      path="purchase-history"
                      element={<PurchaseHistoryPage />}
                    />
                    <Route
                      path="price-variation"
                      element={<PriceVariationPage />}
                    />
                    <Route path="asset-master" element={<AssetMasterPage />} />
                    <Route
                      path="break-summary"
                      element={<BreakSummaryPage />}
                    />
                    <Route
                      path="break-duration"
                      element={<BreakDurationPage />}
                    />
                    <Route
                      path="irregular-breaks"
                      element={<IrregularBreaksPage />}
                    />
                    <Route
                      path="time-deviation"
                      element={<TimeDeviationPage />}
                    />
                    <Route
                      path="tickets-overview"
                      element={<TicketsOverviewPage />}
                    />
                    <Route
                      path="tat-compliance"
                      element={<TatCompliancePage />}
                    />
                    <Route path="escalations" element={<EscalationsPage />} />
                    <Route
                      path="department-tickets"
                      element={<DepartmentTicketsPage />}
                    />
                    <Route
                      path="recurring-issues"
                      element={<RecurringIssuesPage />}
                    />
                    <Route
                      path="handler-productivity"
                      element={<HandlerProductivityPage />}
                    />
                    <Route
                      path="canteen-orders"
                      element={<CanteenOrdersPage />}
                    />
                    <Route
                      path="popular-items"
                      element={<PopularItemsPage />}
                    />
                    <Route
                      path="canteen-finance"
                      element={<CanteenFinancePage />}
                    />
                    <Route
                      path="ingredient-stock"
                      element={<IngredientStockPage />}
                    />
                  </Route>
                </Route>

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </Suspense>
              </RouteErrorBoundary>

              <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
            </div>
          </Router>
        </TooltipProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
